import { type ComponentPropsWithoutRef, type ReactNode } from 'react';

const colorMap = {
  accent: 'bg-action text-action-foreground',
  success: 'bg-status-success text-white',
  warning: 'bg-status-warning text-white',
  error: 'bg-status-error text-white',
  info: 'bg-status-info text-white',
  muted: 'bg-popover text-muted-foreground',
} as const;

const dotColorMap = {
  accent: 'bg-action',
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  error: 'bg-status-error',
  info: 'bg-status-info',
  muted: 'bg-muted-foreground',
} as const;

export type BadgeColor = keyof typeof colorMap;

interface BadgeDotProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
  variant: 'dot';
  color?: BadgeColor;
  children?: never;
}

interface BadgeCountProps extends ComponentPropsWithoutRef<'span'> {
  variant: 'count';
  color?: BadgeColor;
  children: ReactNode;
}

export type BadgeProps = BadgeDotProps | BadgeCountProps;

export function Badge({ variant, color = 'accent', className = '', ...rest }: BadgeProps) {
  if (variant === 'dot') {
    const { children: _c, ...dotRest } = rest as Record<string, unknown>;
    const hasLabel = 'aria-label' in dotRest || 'aria-labelledby' in dotRest;
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${dotColorMap[color]} ${className}`}
        {...(hasLabel ? {} : { 'aria-hidden': true as const })}
        {...dotRest}
      />
    );
  }

  const { children, ...countRest } = rest as BadgeCountProps;
  return (
    <span
      className={[
        'inline-flex items-center justify-center min-w-4 h-4 px-1',
        'rounded-full text-caption leading-none',
        colorMap[color],
        className,
      ].join(' ')}
      {...countRest}
    >
      {children}
    </span>
  );
}
